'use client'

import { buttonClassNames } from '@components/primitives/Button'
import Link from '@components/primitives/Link'
import { classMerge } from '@components/utilities/classMerge'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import PatientRoutes from '@lib/routes/PatientRoutes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { AlgoliaSearchResultAndUiState } from '@modules/trials/search/getDataFromAlgolia'
import { use, useEffect, useState } from 'react'

export function MatchMeButton({
  dataFromAlgoliaPromise,
}: {
  dataFromAlgoliaPromise: Promise<AlgoliaSearchResultAndUiState>
}) {
  const {
    algoliaResponse: { hits },
  } = use(dataFromAlgoliaPromise)

  const [isVisible, setIsVisible] = useState(true)

  const SENTINEL_ID = 'match-button-sentinel'

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          // Hide the sticky button when the sentinel (bottom of page) comes into view
          setIsVisible(!entry.isIntersecting)
        }
      },
      {
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      },
    )

    const sentinelElement = document.getElementById(SENTINEL_ID)
    if (sentinelElement) {
      observer.observe(sentinelElement)
    }

    return () => observer.disconnect()
  }, [])

  const trial = hits[0]

  if (!trial || !trial.slug) {
    return null
  }

  return (
    <>
      <div className='right-0 top-0 hidden pr-4 pt-3 lg:absolute lg:right-8 lg:top-4 lg:block lg:pr-0 lg:pt-0'>
        <Link
          className={classMerge(
            buttonClassNames.legacy.fill.black,
            'h-9 w-full rounded-lg antialiased lg:h-[62px]',
          )}
          href={absoluteUrl(PatientRoutes.trialApplyBeginGeneric(trial.slug))}
          newTab
          onClick={() => {
            trackEvent(TrackingEvents.SEARCH_LANDER_MATCH_TO_TRIAL_CLICKED)
          }}
        >
          <span className='mx-5 text-base font-semibold text-white antialiased lg:mx-10'>
            Match to a Trial
          </span>
        </Link>
      </div>
      <div
        className={classMerge(
          'sticky bottom-0 z-10 w-full bg-white p-4 shadow-[0_-0px_16px_rgba(0,0,0,0.20)] transition-opacity duration-300 lg:hidden',
          isVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <Link
          className={classMerge(
            buttonClassNames.primary.black,
            'w-full font-bold tracking-wide antialiased',
          )}
          href={absoluteUrl(PatientRoutes.trialApplyBeginGeneric(trial.slug))}
          newTab
          onClick={() => {
            trackEvent(TrackingEvents.SEARCH_LANDER_MATCH_TO_TRIAL_CLICKED)
          }}
        >
          Match to a Trial
        </Link>
      </div>
      {/* Sentinel element to detect when user reaches bottom */}
      <div className='h-1 w-full' id={SENTINEL_ID} />
    </>
  )
}
